/**
 * The assessment pack — chunk 8 (`server/app/routers/pack.py`,
 * `server/app/services/pack.py`). Two model calls over a matter's latest
 * run — an offer email, tiered client questions — linted, then a
 * **manual** approve/dispatch that this module never automates: dispatch
 * records that a person already sent the pack from their own mail client,
 * and transmits nothing itself (I8's sibling rule for this chunk).
 *
 * Kept out of `pipeline.ts` for the reason that module's own docstring gives
 * for keeping matters and runs together and nothing else: the pack is one
 * more resource hanging off a matter, but it is not part of *reading a run*,
 * and a card that needs it should not have to pull in the whole pipeline
 * vocabulary to get it.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'

import { api } from './client'

/** `OfferOut` — the drafted engagement email, never a template this reads. */
export interface OfferEmail {
  text: string
  office: string
  grounds: string[]
  fee: string
  base_rates: string
  rate_sources: Record<string, unknown>[]
  variant: string
}

/** `QuestionsOut` — the tiered client questions, §7.1's own polarity split. */
export interface ClientQuestions {
  text: string
  office: string
  grounds: string[]
  checklist: string[]
  markers: string[]
  variant: string
}

/** `LintFindingOut`. WARN only — see `server/app/offers/lint.py`'s docstring
 * on why a client-facing sales doc manually dispatched has no BLOCK tier. */
export interface PackLintFinding {
  severity: string
  code: string
  detail: string
}

/**
 * `PackOut`. `approved`/`dispatched` are derived fresh by the server on
 * every read (`_approval_state`, the same reason `PipelineRun.signed` is) —
 * never cached here, so a page reloaded after an approval elsewhere still
 * shows the truth.
 */
export interface AssessmentPack {
  matter_id: string
  run_id: string
  offer: OfferEmail
  questions: ClientQuestions
  lint: PackLintFinding[]
  rendered_event_id: number
  approved: boolean
  dispatched: boolean
}

/** `PackIn`. Supplied fresh on every render — see `services/pack.py`'s
 * module docstring on why the office action is not a stored column. */
export interface RenderPackRequest {
  office_action: string
  case_facts?: string
  posture?: string
  info_needed?: string
}

/** `ApprovalOut`. */
export interface ApprovalResult {
  matter_id: string
  pack_event_id: number
  approved_event_id: number
}

/** `DispatchOut`. */
export interface DispatchResult {
  matter_id: string
  approved_event_id: number
  dispatched_event_id: number
  recipients: string[]
}

export const packKeys = {
  pack: (matterId: string) => ['matters', matterId, 'pack'] as const,
}

function packUrl(matterId: string): string {
  return `/api/matters/${encodeURIComponent(matterId)}/pack`
}

/**
 * The last rendered pack, if any.
 *
 * `retry: false` — a 404 here means "nothing rendered yet", which is a
 * legitimate, expected answer for a matter with a run and no pack, not a
 * transient failure to retry through. The caller reads `error.isNotFound`
 * ({@link ApiError}) to tell that apart from a real failure.
 */
export function useMatterPack(matterId: string | null): UseQueryResult<AssessmentPack> {
  return useQuery<AssessmentPack>({
    queryKey: packKeys.pack(matterId ?? ''),
    queryFn: () => api.get<AssessmentPack>(packUrl(matterId ?? '')),
    enabled: Boolean(matterId),
    retry: false,
  })
}

/** Render (or re-render) the pack over the matter's latest run. */
export function useRenderPack() {
  const queryClient = useQueryClient()
  return useMutation<AssessmentPack, unknown, { matterId: string; body: RenderPackRequest }>({
    mutationFn: ({ matterId, body }) => api.post<AssessmentPack>(packUrl(matterId), body),
    onSuccess: (_pack, { matterId }) => {
      void queryClient.invalidateQueries({ queryKey: packKeys.pack(matterId) })
    },
  })
}

/** Human-only server-side (`current_human`) — see `useSignRun`'s note in
 * `pipeline.ts` for what that means for this hook when the caller is a key. */
export function useApprovePack() {
  const queryClient = useQueryClient()
  return useMutation<ApprovalResult, unknown, string>({
    mutationFn: (matterId) => api.post<ApprovalResult>(`${packUrl(matterId)}/approve`),
    onSuccess: (_result, matterId) => {
      void queryClient.invalidateQueries({ queryKey: packKeys.pack(matterId) })
    },
  })
}

/** Records a send that already happened elsewhere. Transmits nothing. */
export function useDispatchPack() {
  const queryClient = useQueryClient()
  return useMutation<DispatchResult, unknown, { matterId: string; recipients: string[] }>({
    mutationFn: ({ matterId, recipients }) =>
      api.post<DispatchResult>(`${packUrl(matterId)}/dispatch`, { recipients }),
    onSuccess: (_result, { matterId }) => {
      void queryClient.invalidateQueries({ queryKey: packKeys.pack(matterId) })
    },
  })
}
